// ไฟล์: frontend/src/pages/ArticleDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ArticleDetailPage.css';

const ArticleDetailPage = ({ user }) => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [userRating, setUserRating] = useState(0);
  const [inLibrary, setInLibrary] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticleData = async () => {
      try {
        // ดึงข้อมูลบทความ
        const response = await axios.get(`/articles/${id}`);
        setArticle(response.data.article);
        setComments(response.data.comments);
        
        // ดึงข้อมูลเพิ่มเติมถ้ามีการล็อกอิน
        if (user) {
          // ดึงคะแนนของผู้ใช้
          const ratingResponse = await axios.get(`/ratings/user?article_id=${id}`);
          setUserRating(ratingResponse.data.rating || 0);
          
          // ตรวจสอบว่าอยู่ในคลังหรือไม่
          const libraryResponse = await axios.get('/library');
          const isInLibrary = libraryResponse.data.items.some(
            item => item.article_id === parseInt(id)
          );
          setInLibrary(isInLibrary);
          
          // ตรวจสอบสถานะการติดตามผู้เขียน
          if (response.data.article.user_id) {
            try {
              const followResponse = await axios.get(`/follow/status/${response.data.article.user_id}`);
              setFollowed(followResponse.data.followed);
            } catch (error) {
              console.error('Error checking follow status:', error);
            }
          }
        }
        
        // ดึงบทความที่เกี่ยวข้อง
        const relatedResponse = await axios.get('/articles', {
          params: {
            category_id: response.data.article.category_id,
            limit: 3,
            exclude_id: id
          }
        });
        setRelatedArticles(relatedResponse.data.articles.slice(0, 3));
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching article data:', error);
        setLoading(false);
      }
    };
    
    fetchArticleData();
    // ย้อนกลับไปด้านบนของหน้า
    window.scrollTo(0, 0);
  }, [id, user]);

  // ให้คะแนนบทความ
  const handleRate = async (rating) => {
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนให้คะแนน',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }

    try {
      await axios.post('/ratings', {
        rating,
        article_id: id
      });
      
      setUserRating(rating);
      
      // อัพเดทคะแนนเฉลี่ยในข้อมูลบทความ
      setArticle(prev => ({
        ...prev,
        avg_rating: prev.rating_count > 0 
          ? ((prev.avg_rating * prev.rating_count) + rating) / (prev.rating_count + 1)
          : rating,
        rating_count: prev.rating_count + 1
      }));
      
      Swal.fire({
        title: 'ให้คะแนนสำเร็จ',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error rating article:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถให้คะแนนได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // เพิ่มความคิดเห็น
  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนแสดงความคิดเห็น',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }
    
    if (!commentText.trim()) {
      Swal.fire({
        title: 'ข้อความว่างเปล่า',
        text: 'กรุณาป้อนข้อความก่อนส่ง',
        icon: 'warning'
      });
      return;
    }
    
    try {
      const response = await axios.post('/comments', {
        content: commentText,
        article_id: id
      });
      
      setComments([response.data.comment, ...comments]);
      setCommentText('');
      
      Swal.fire({
        title: 'ความคิดเห็นถูกเพิ่มแล้ว',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถเพิ่มความคิดเห็นได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // ลบความคิดเห็น
  const handleDeleteComment = async (commentId) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณต้องการลบความคิดเห็นนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/comments/${commentId}`);
          setComments(comments.filter(comment => comment.id !== commentId));
          
          Swal.fire({
            title: 'ลบความคิดเห็นสำเร็จ',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });
        } catch (error) {
          console.error('Error deleting comment:', error);
          Swal.fire({
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบความคิดเห็นได้ในขณะนี้',
            icon: 'error'
          });
        }
      }
    });
  };

  // บันทึกในคลัง
  const handleToggleLibrary = async () => {
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนบันทึกในคลัง',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }
    
    try {
      if (inLibrary) {
        // ค้นหา ID ของรายการในคลัง
        const libraryResponse = await axios.get('/library');
        const libraryItem = libraryResponse.data.items.find(
          item => item.article_id === parseInt(id)
        );
        
        if (libraryItem) {
          await axios.delete(`/library/${libraryItem.library_id}`);
          setInLibrary(false);
          
          Swal.fire({
            title: 'นำออกจากคลังสำเร็จ',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });
        }
      } else {
        await axios.post('/library', {
          article_id: id,
          status: 'saved'
        });
        
        setInLibrary(true);
        
        Swal.fire({
          title: 'บันทึกในคลังสำเร็จ',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error toggling library:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถดำเนินการได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // ติดตาม/เลิกติดตามผู้เขียน
  const handleToggleFollow = async () => {
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนติดตาม',
        icon: 'info',
        confirmButtonText: 'เข้าสู่ระบบ',
        showCancelButton: true,
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '/login';
        }
      });
      return;
    }
    
    try {
      const response = await axios.post('/follow', {
        teacherId: article.user_id
      });
      
      setFollowed(response.data.followed);
      
      Swal.fire({
        title: response.data.followed ? 'ติดตามสำเร็จ' : 'เลิกติดตามสำเร็จ',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error toggling follow:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถดำเนินการได้ในขณะนี้',
        icon: 'error'
      });
    }
  };

  // แชร์บทความ
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.content?.substring(0, 100),
        url: window.location.href
      }).catch(err => {
        console.error('Error sharing:', err);
      });
    } else {
      // สำหรับเบราว์เซอร์ที่ไม่รองรับ Web Share API
      Swal.fire({
        title: 'แชร์บทความนี้',
        html: `
          <div class="share-options">
            <a href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}" target="_blank" class="share-option facebook">
              <i class="fab fa-facebook-f"></i> Facebook
            </a>
            <a href="https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}" target="_blank" class="share-option twitter">
              <i class="fab fa-twitter"></i> Twitter
            </a>
            <a href="https://line.me/R/msg/text/?${encodeURIComponent(article.title + '\n' + window.location.href)}" target="_blank" class="share-option line">
              <i class="fab fa-line"></i> Line
            </a>
            <button class="share-option copy" id="copy-link">
              <i class="fas fa-link"></i> คัดลอกลิงก์
            </button>
          </div>
        `,
        showConfirmButton: false,
        showCloseButton: true,
        didOpen: () => {
          const copyButton = document.getElementById('copy-link');
          if (copyButton) {
            copyButton.addEventListener('click', () => {
              navigator.clipboard.writeText(window.location.href).then(() => {
                Swal.fire({
                  title: 'คัดลอกลิงก์แล้ว',
                  icon: 'success',
                  timer: 1500,
                  showConfirmButton: false
                });
              });
            });
          }
        }
      });
    }
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  // ถ้าไม่พบบทความ
  if (!article) {
    return (
      <div className="container">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i> ไม่พบบทความที่คุณต้องการ
        </div>
      </div>
    );
  }

  return (
    <div className="article-detail-page">
      <div className="container">
        <div className="article-header">
          <div className="article-category">
            <Link to={`/articles?category=${article.category_id}`}>
              <i className="fas fa-folder"></i> {article.category_name}
            </Link>
          </div>
          
          <h1 className="article-title">{article.title}</h1>
          
          <div className="article-meta">
            <div className="article-author">
              <Link to={`/teachers/${article.user_id}`}>
                <img 
                  src={article.author_image 
                    ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${article.author_image}`
                    : `https://ui-avatars.com/api/?name=${article.author_name}&background=random`} 
                  alt={article.author_name} 
                />
                <span>{article.author_name}</span>
              </Link>
              
              {user && user.id !== article.user_id && (
                <button 
                  className={`follow-button ${followed ? 'following' : ''}`}
                  onClick={handleToggleFollow}
                >
                  <i className={`fas ${followed ? 'fa-user-check' : 'fa-user-plus'}`}></i>
                  <span>{followed ? 'กำลังติดตาม' : 'ติดตาม'}</span>
                </button>
              )}
            </div>
            
            <div className="article-info">
              <div className="article-date">
                <i className="far fa-calendar-alt"></i>
                <span>{new Date(article.created_at).toLocaleDateString('th-TH')}</span>
              </div>
              
              <div className="article-views">
                <i className="fas fa-eye"></i>
                <span>{article.view_count} ครั้ง</span>
              </div>
              
              <div className="article-comments-count">
                <i className="fas fa-comments"></i>
                <span>{comments.length} ความคิดเห็น</span>
              </div>
            </div>
          </div>
        </div>
        
        {article.thumbnail && (
          <div className="article-featured-image">
            <img 
              src={`${process.env.REACT_APP_API_URL}/uploads/${article.thumbnail}`} 
              alt={article.title} 
            />
          </div>
        )}
        
        <div className="article-detail-layout">
          <div className="article-content">
            <div className="article-body">
              {article.content ? (
                <div dangerouslySetInnerHTML={{ __html: article.content.replace(/\n/g, '<br>') }}></div>
              ) : (
                <p>ไม่มีเนื้อหา</p>
              )}
            </div>
            
            <div className="article-actions">
              <div className="article-rating">
                <div className="rating-label">ให้คะแนนบทความนี้:</div>
                <div className="rating-stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <i 
                      key={star}
                      className={`fas fa-star ${star <= userRating ? 'active' : ''}`}
                      onClick={() => handleRate(star)}
                    ></i>
                  ))}
                                 <span className="rating-count">
                  ({article.rating_count} คะแนน | เฉลี่ย {
                    article.avg_rating !== undefined &&
                    article.avg_rating !== null &&
                    !isNaN(Number(article.avg_rating))
                      ? Number(article.avg_rating).toFixed(1)
                      : '0.0'
                  })
                </span>
                </div>
              </div>
              
              <div className="article-buttons">
                <button 
                  className={`btn ${inLibrary ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                  onClick={handleToggleLibrary}
                >
                  <i className={`fas ${inLibrary ? 'fa-trash' : 'fa-bookmark'}`}></i> 
                  {inLibrary ? 'นำออกจากคลัง' : 'บันทึกในคลัง'}
                </button>
                
                <button 
                  className="btn btn-outline-secondary"
                  onClick={handleShare}
                >
                  <i className="fas fa-share-alt"></i> แชร์
                </button>
              </div>
            </div>
            
            <div className="article-tags">
              <div className="tags-label">แท็ก:</div>
              <div className="tags-list">
                <span className="tag-item">{article.category_name}</span>
                <span className="tag-item">บทความ</span>
                <span className="tag-item">แนวคิด</span>
              </div>
            </div>
            
            <div className="article-author-box">
              <div className="author-avatar">
                <img 
                  src={article.author_image 
                    ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${article.author_image}`
                    : `https://ui-avatars.com/api/?name=${article.author_name}&background=random&size=200`} 
                  alt={article.author_name} 
                />
              </div>
              
              <div className="author-info">
                <h3 className="author-name">
                  <Link to={`/teachers/${article.user_id}`}>{article.author_name}</Link>
                </h3>
                
                <div className="author-bio">
                  {article.author_bio || 'ครูผู้สอนและนักเขียนบทความ'}
                </div>
                
                {user && user.id !== article.user_id && (
                  <button 
                    className={`btn ${followed ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={handleToggleFollow}
                  >
                    <i className={`fas ${followed ? 'fa-user-check' : 'fa-user-plus'}`}></i> 
                    {followed ? 'กำลังติดตาม' : 'ติดตาม'}
                  </button>
                )}
              </div>
            </div>
            
            <div className="comments-section">
              <h2 className="comments-title">
                <i className="fas fa-comments"></i> ความคิดเห็น ({comments.length})
              </h2>
              
              <div className="comment-form">
                <form onSubmit={handleAddComment}>
                  <div className="form-group">
                    <textarea 
                      className="form-control"
                      placeholder="แสดงความคิดเห็นของคุณ..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows="3"
                    ></textarea>
                  </div>
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-paper-plane"></i> ส่งความคิดเห็น
                  </button>
                </form>
              </div>
              
              <div className="comment-list">
                {comments.length > 0 ? (
                  comments.map(comment => (
                    <div className="comment" key={comment.id}>
                      <img 
                        src={comment.profile_image 
                          ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${comment.profile_image}`
                          : `https://ui-avatars.com/api/?name=${comment.fullname}&background=random`} 
                        alt={comment.fullname} 
                        className="comment-avatar"
                      />
                      <div className="comment-content">
                        <div className="comment-header">
                          <div className="comment-author">{comment.fullname}</div>
                          <div className="comment-date">
                            {new Date(comment.created_at).toLocaleDateString('th-TH')}
                          </div>
                        </div>
                        <div className="comment-text">{comment.content}</div>
                        <div className="comment-actions">
                          {user && (user.id === comment.user_id || user.role === 'admin') && (
                            <div 
                              className="comment-action"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <i className="fas fa-trash"></i> ลบ
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-comments">ยังไม่มีความคิดเห็น เป็นคนแรกที่แสดงความคิดเห็นสิ!</div>
                )}
              </div>
            </div>
          </div>
          
          <div className="article-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-card-title">
                <i className="fas fa-pen-fancy"></i> บทความที่เกี่ยวข้อง
              </div>
              <div className="related-articles">
                {relatedArticles.length > 0 ? (
                  relatedArticles.map(item => (
                    <div className="related-article" key={item.id}>
                      <img 
                        src={item.thumbnail 
                          ? `${process.env.REACT_APP_API_URL}/uploads/${item.thumbnail}`
                          : '/assets/default-article.jpg'} 
                        alt={item.title} 
                        className="related-article-image"
                      />
                      <div className="related-article-info">
                        <Link to={`/articles/${item.id}`} className="related-article-title">
                          {item.title}
                        </Link>
                        <div className="related-article-meta">
                          <div className="related-article-author">
                            โดย {item.author_name}
                          </div>
                          <div className="related-article-date">
                            {new Date(item.created_at).toLocaleDateString('th-TH')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-related">ไม่พบบทความที่เกี่ยวข้อง</div>
                )}
              </div>
            </div>
            
            <div className="sidebar-card">
              <div className="sidebar-card-title">
                <i className="fas fa-share-alt"></i> แชร์บทความนี้
              </div>
              <div className="social-share-buttons">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-button facebook"
                >
                  <i className="fab fa-facebook-f"></i> Facebook
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-button twitter"
                >
                  <i className="fab fa-twitter"></i> Twitter
                </a>
                <a 
                  href={`https://line.me/R/msg/text/?${encodeURIComponent(article.title + '\n' + window.location.href)}`} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-button line"
                >
                  <i className="fab fa-line"></i> Line
                </a>
                <button 
                  className="share-button copy"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href).then(() => {
                      Swal.fire({
                        title: 'คัดลอกลิงก์แล้ว',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false
                      });
                    });
                  }}
                >
                  <i className="fas fa-link"></i> คัดลอกลิงก์
                </button>
              </div>
            </div>
            
            <div className="sidebar-card">
              <div className="sidebar-card-title">
                <i className="fas fa-lightbulb"></i> บทความล่าสุด
              </div>
              <div className="latest-articles">
                <Link to="/articles" className="btn btn-primary btn-block">
                  <i className="fas fa-list"></i> ดูบทความทั้งหมด
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
